import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PITCH_DIMENSIONS } from "@Components/game/session-results/feedback/visualization/constants";
import { Events, UnityBridge } from "@Context/common/general/context";
import { AnalyticsEvents } from "@Enums/index";
import { IncomingReplayEvent, IncomingReplayEventData } from "@Globals/types";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { getDecisionCardTime, getFirstUserDecision, type ReplayDecision } from "@hooks/common/session/replay-decision-utils";
import type { SessionResultsScenarioAnalyticsData } from "@hooks/game/activity/session/session-results-analytics";
import useSessionReplayStream, { ReplayStreamActions, ReplayUiFeatures } from "@hooks/game/activity/session/use-session-replay-stream";
import type { ReplayData } from "@Shared/types/ReplayData";
import { isNumber } from "lodash-es";

interface ReplayTarget {
  cognitive_result_id: number;
  retry_attempt_id?: string;
}

interface IStreamUi {
  cognitiveResultId: number;
  retryAttemptId?: string;
  decisions: ReplayDecision[];
  replayData: ReplayData | null;
  isUnityReplayReady: boolean;
  scenarioAnalytics: SessionResultsScenarioAnalyticsData | null;
}

interface SessionPlaybackState {
  videoDuration: number;
  isPlaying: boolean;
  progress: number;
}

interface SessionPlaybackUiState {
  show2DView: boolean;
  showVisionCone: boolean;
  showHeatmap: boolean;
  showSettings: boolean;
  isDecisionPanelExpanded: boolean;
  selectedCard: number;
  highlightPointIndex: number;
  isUnityReplayReady: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  containerDimensions: { width: number; height: number };
}

interface SeekOptions {
  syncUnity?: boolean;
}

interface HeatmapToggleOptions {
  trackEvent?: boolean;
}

type ExitReplayModePayload = {
  time?: unknown;
  cognitive_result_id?: number;
  retry_attempt_id?: string;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const upperBound = (sorted: number[], value: number) => {
  let low = 0;
  let high = sorted.length;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (sorted[mid] <= value) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return low;
};

const normalizeReplayProgressSeconds = (rawProgress: number, duration: number) => {
  if (!Number.isFinite(rawProgress) || rawProgress < 0) {
    return 0;
  }

  if (duration <= 0) {
    return rawProgress;
  }

  if (rawProgress <= 1) {
    return clamp(rawProgress * duration, 0, duration);
  }

  if (rawProgress <= duration + 0.5) {
    return clamp(rawProgress, 0, duration);
  }

  if (rawProgress <= 100) {
    return clamp((rawProgress / 100) * duration, 0, duration);
  }

  if (rawProgress / 1000 <= duration + 0.5) {
    return clamp(rawProgress / 1000, 0, duration);
  }

  return clamp(rawProgress, 0, duration);
};

export const useStreamUi = ({ cognitiveResultId, retryAttemptId, decisions, replayData, isUnityReplayReady, scenarioAnalytics }: IStreamUi) => {
  const { track } = useAnalytics();
  const { dispatchReplayWebEvent } = useSessionReplayStream(isUnityReplayReady);
  const replayTarget = useMemo<ReplayTarget>(
    () => ({ cognitive_result_id: cognitiveResultId, ...(retryAttemptId ? { retry_attempt_id: retryAttemptId } : {}) }),
    [cognitiveResultId, retryAttemptId]
  );
  const [videoDuration, setVideoDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [show2DView, setShow2DView] = useState(true);
  const [showVisionCone, setShowVisionCone] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isDecisionPanelExpanded, setIsDecisionPanelExpanded] = useState(true);
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [highlightPointIndex, setHighlightPointIndex] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number }>({
    width: PITCH_DIMENSIONS.WIDTH,
    height: PITCH_DIMENSIONS.HEIGHT,
  });

  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimestampRef = useRef<number | null>(null);
  const isInFullscreenModeRef = useRef<boolean>(false);
  const hasInitializedFromDecisionsRef = useRef<boolean>(false);
  const progressRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  const videoDurationRef = useRef<number>(0);

  const decisionSnapTimes = useMemo(
    () =>
      decisions
        .map(decision => getDecisionCardTime(decision, 0))
        .filter((time): time is number => typeof time === "number" && Number.isFinite(time))
        .sort((a, b) => a - b),
    [decisions]
  );

  const decisionStartTimes = useMemo(
    () =>
      decisions
        .map(decision => decision.startTime)
        .filter((time): time is number => typeof time === "number" && Number.isFinite(time))
        .sort((a, b) => a - b),
    [decisions]
  );

  const stopAnimation = useCallback(() => {
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    lastFrameTimestampRef.current = null;
  }, []);

  const applyProgress = useCallback((value: number) => {
    const duration = videoDurationRef.current;
    const clamped = duration > 0 ? clamp(value, 0, duration) : Math.max(value, 0);

    if (clamped === progressRef.current) {
      return;
    }

    progressRef.current = clamped;
    setProgress(clamped);
  }, []);

  const seekTo = useCallback(
    (target: number, options?: SeekOptions) => {
      const syncUnity = options?.syncUnity ?? true;

      setIsPlaying(false);
      stopAnimation();
      applyProgress(target);

      if (syncUnity && !isInFullscreenModeRef.current) {
        dispatchReplayWebEvent(UnityBridge.OutgoingWebEvents.ReplayEvent, {
          action: ReplayStreamActions.SEEK,
          data: {
            ...replayTarget,
            time: Math.max(0, target),
          },
        });
      }
    },
    [applyProgress, dispatchReplayWebEvent, replayTarget, stopAnimation]
  );

  const trackScenarioEvent = useCallback(
    (eventType: string, data?: Record<string, unknown>) => {
      const eventData = {
        ...(scenarioAnalytics ?? {}),
        ...(data ?? {}),
      };

      track(eventType, eventData);
    },
    [scenarioAnalytics, track]
  );

  const handleToggle2DView = useCallback(() => {
    setShow2DView(prev => !prev);
  }, []);

  const handleToggleVisionCone = useCallback(() => {
    setShowVisionCone(prev => {
      const next = !prev;

      trackScenarioEvent(AnalyticsEvents.vision_cone_toggled, { enabled: next });

      return next;
    });
  }, [trackScenarioEvent]);

  const handleToggleHeatmap = useCallback(
    (value: boolean, options?: HeatmapToggleOptions) => {
      setShowHeatmap(value);

      if (options?.trackEvent ?? true) {
        trackScenarioEvent(AnalyticsEvents.heatmap_toggled, { enabled: value });
      }
    },
    [trackScenarioEvent]
  );

  const handlePlayPause = useCallback(() => {
    if (progressRef.current >= videoDurationRef.current && videoDurationRef.current > 0) {
      applyProgress(0);
      setHighlightPointIndex(0);
    }

    const next = !isPlayingRef.current;
    isPlayingRef.current = next;
    setIsPlaying(next);

    if (next) {
      trackScenarioEvent(AnalyticsEvents.replay_2d_started);
    }
  }, [applyProgress, trackScenarioEvent]);

  const handleFullscreen = useCallback(() => {
    if (!isUnityReplayReady) return;
    isInFullscreenModeRef.current = true;
    trackScenarioEvent(AnalyticsEvents.replay_3d_opened);

    dispatchReplayWebEvent(UnityBridge.OutgoingWebEvents.ReplayEvent, {
      action: ReplayStreamActions.START,
      data: {
        ...replayTarget,
        time: progressRef.current,
      },
    });
  }, [dispatchReplayWebEvent, isUnityReplayReady, replayTarget, trackScenarioEvent]);

  const handleExitReplayMode = useCallback(
    (payload: unknown) => {
      if (!isUnityReplayReady) return;
      const maybePayload = (payload as ExitReplayModePayload | null) ?? null;
      if ((maybePayload?.retry_attempt_id || undefined) !== (retryAttemptId || undefined)) return;
      if (maybePayload?.cognitive_result_id !== undefined && maybePayload.cognitive_result_id !== cognitiveResultId) return;
      const time = maybePayload?.time;
      dispatchReplayWebEvent(UnityBridge.OutgoingWebEvents.ReplayEvent, {
        action: ReplayStreamActions.END_STREAM,
        data: replayTarget,
      });

      if (!isNumber(time)) {
        isInFullscreenModeRef.current = false;
        return;
      }

      applyProgress(time);
      setIsPlaying(false);
      isInFullscreenModeRef.current = false;
    },
    [applyProgress, cognitiveResultId, dispatchReplayWebEvent, isUnityReplayReady, replayTarget, retryAttemptId]
  );

  useEffect(() => {
    stopAnimation();
    isInFullscreenModeRef.current = false;
    hasInitializedFromDecisionsRef.current = false;
    setIsPlaying(false);
    setShowSettings(false);
    setSelectedCard(0);
    setHighlightPointIndex(0);
    setVideoDuration(0);
    videoDurationRef.current = 0;
    applyProgress(0);
  }, [applyProgress, replayData, replayTarget, stopAnimation]);

  useEffect(() => {
    const duration = replayData?.duration ?? 0;
    if (duration === videoDurationRef.current) {
      return;
    }

    setVideoDuration(duration);
    videoDurationRef.current = duration;
    applyProgress(progressRef.current);
  }, [applyProgress, replayData, replayTarget]);

  useEffect(() => {
    setSelectedCard(0);

    const lastDecisionIndex = Math.max(0, decisions.length - 1);
    setHighlightPointIndex(index => Math.min(index, lastDecisionIndex));
  }, [decisions]);

  useEffect(() => {
    if (hasInitializedFromDecisionsRef.current || decisions.length <= 0) {
      return;
    }

    hasInitializedFromDecisionsRef.current = true;
    handleToggleHeatmap(true, { trackEvent: false });

    const firstUserOption = getFirstUserDecision(decisions);
    if (!replayData) {
      seekTo(0, { syncUnity: false });
      return;
    }

    if (!firstUserOption) {
      seekTo(getDecisionCardTime(decisions[0], 0), { syncUnity: false });
      return;
    }

    const startingProgress = firstUserOption.evalTime ?? 0;
    seekTo(startingProgress, { syncUnity: false });
  }, [decisions, handleToggleHeatmap, replayData, seekTo]);

  useEffect(() => {
    if (!isUnityReplayReady) return;

    dispatchReplayWebEvent(UnityBridge.OutgoingWebEvents.ReplayUiEvent, {
      action: "toggle",
      ...replayTarget,
      feature: ReplayUiFeatures.VISION_CONE,
      value: showVisionCone,
    });
  }, [dispatchReplayWebEvent, isUnityReplayReady, replayTarget, showVisionCone]);

  useEffect(() => {
    if (!isUnityReplayReady) return;

    dispatchReplayWebEvent(UnityBridge.OutgoingWebEvents.ReplayUiEvent, {
      action: "toggle",
      ...replayTarget,
      feature: ReplayUiFeatures.HEATMAP,
      value: showHeatmap,
    });
  }, [dispatchReplayWebEvent, isUnityReplayReady, replayTarget, showHeatmap]);

  useEffect(() => {
    if (!show2DView || !containerRef.current) {
      return;
    }

    const updateDimensions = () => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setContainerDimensions({ width: rect.width, height: rect.height });
      }
    };

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);
    updateDimensions();

    return () => {
      resizeObserver.disconnect();
    };
  }, [show2DView]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    const animate = (timestamp: number) => {
      if (!isPlayingRef.current) {
        return;
      }

      if (lastFrameTimestampRef.current == null) {
        lastFrameTimestampRef.current = timestamp;
      }

      const deltaSeconds = (timestamp - lastFrameTimestampRef.current) / 1000;
      lastFrameTimestampRef.current = timestamp;

      let nextProgress = progressRef.current + deltaSeconds;

      if (decisionSnapTimes.length > 0) {
        const nextDecisionIndex = upperBound(decisionSnapTimes, progressRef.current);
        const nextDecisionTime = decisionSnapTimes[nextDecisionIndex];
        if (typeof nextDecisionTime === "number" && nextDecisionTime - progressRef.current > 0 && nextDecisionTime - progressRef.current < 0.075) {
          nextProgress = nextDecisionTime;
        }
      }

      const duration = videoDurationRef.current;
      if (duration > 0 && nextProgress >= duration) {
        applyProgress(duration);
        setIsPlaying(false);
        stopAnimation();
        return;
      }

      applyProgress(nextProgress);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (!isPlaying) {
      stopAnimation();
      return;
    }

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      stopAnimation();
    };
  }, [applyProgress, decisionSnapTimes, isPlaying, stopAnimation]);

  useEffect(() => {
    if (decisionStartTimes.length === 0) {
      if (highlightPointIndex !== 0) {
        setHighlightPointIndex(0);
      }
      return;
    }

    const nextIndex = Math.max(0, Math.min(upperBound(decisionStartTimes, progress) - 1, decisionStartTimes.length - 1));
    if (nextIndex !== highlightPointIndex) {
      setHighlightPointIndex(nextIndex);
    }
  }, [decisionStartTimes, highlightPointIndex, progress]);

  useEffect(() => {
    const handleReplayProgress = (event: IncomingReplayEvent) => {
      if (!isUnityReplayReady || event.action !== "progress") {
        return;
      }

      const replayProgressItems = Array.isArray(event.data) ? event.data : event.data ? [event.data] : [];
      const progressData = replayProgressItems.find(
        (data: IncomingReplayEventData) =>
          data.cognitive_result_id === replayTarget.cognitive_result_id &&
          (data.retry_attempt_id || event.retry_attempt_id || undefined) === (replayTarget.retry_attempt_id || undefined)
      );
      if (!progressData) {
        return;
      }

      const duration = videoDurationRef.current;
      const nextProgress = normalizeReplayProgressSeconds(progressData.progress ?? 0, duration);
      applyProgress(nextProgress);

      if (progressData.playback_state === "paused") {
        setIsPlaying(false);
      }
    };

    Events.app.replayEvent.on(handleReplayProgress);
    return () => {
      Events.app.replayEvent.remove(handleReplayProgress);
    };
  }, [applyProgress, isUnityReplayReady, replayTarget]);

  useEffect(() => {
    Events.app.exitReplayMode.on(handleExitReplayMode);

    return () => {
      Events.app.exitReplayMode.remove(handleExitReplayMode);
    };
  }, [handleExitReplayMode]);

  useEffect(() => {
    return () => {
      stopAnimation();
      Events.app.showBreadcrumbs.emit();
    };
  }, [stopAnimation]);

  const handleProgressClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = x / rect.width;
      const time = percentage * videoDurationRef.current;

      seekTo(time);
    },
    [seekTo]
  );

  const handleHighlightClick = useCallback(
    (index: number) => {
      const decision = decisions[index];
      const optionTime = decision ? getDecisionCardTime(decision, selectedCard) : 0;

      setHighlightPointIndex(index);
      seekTo(optionTime);
    },
    [decisions, seekTo, selectedCard]
  );

  const handlePreviousDecision = useCallback(() => {
    if (highlightPointIndex <= 0 || decisions.length === 0) {
      return;
    }

    const newIndex = highlightPointIndex - 1;
    const optionTime = getDecisionCardTime(decisions[newIndex], selectedCard);

    setHighlightPointIndex(newIndex);
    seekTo(optionTime);
  }, [decisions, highlightPointIndex, seekTo, selectedCard]);

  const handleNextDecision = useCallback(() => {
    if (highlightPointIndex >= decisions.length - 1 || decisions.length === 0) {
      return;
    }

    const newIndex = highlightPointIndex + 1;
    const optionTime = getDecisionCardTime(decisions[newIndex], selectedCard);

    setHighlightPointIndex(newIndex);
    seekTo(optionTime);
  }, [decisions, highlightPointIndex, seekTo, selectedCard]);

  const handleToggleSettings = useCallback(() => {
    setShowSettings(prev => !prev);
  }, []);

  const handleToggleDecisionPanel = useCallback(() => {
    setIsDecisionPanelExpanded(prev => {
      const next = !prev;

      trackScenarioEvent(AnalyticsEvents.decision_score_panel_toggled, { hidden: !next });

      return next;
    });
  }, [trackScenarioEvent]);

  const handleSelectCard = useCallback((index: number) => {
    setSelectedCard(index);
  }, []);

  const playbackState = useMemo(
    (): SessionPlaybackState => ({
      videoDuration,
      isPlaying,
      progress,
    }),
    [isPlaying, progress, videoDuration]
  );

  const playbackUiState = useMemo(
    (): SessionPlaybackUiState => ({
      show2DView,
      showVisionCone,
      showHeatmap,
      showSettings,
      isDecisionPanelExpanded,
      selectedCard,
      highlightPointIndex,
      isUnityReplayReady,
      containerRef,
      containerDimensions,
    }),
    [
      containerDimensions,
      highlightPointIndex,
      isDecisionPanelExpanded,
      isUnityReplayReady,
      selectedCard,
      show2DView,
      showHeatmap,
      showSettings,
      showVisionCone,
    ]
  );

  const playbackActions = useMemo(
    () => ({
      setIsPlaying,
      seekTo,
      handlePlayPause,
      handleFullscreen,
      handleProgressClick,
      handleToggle2DView,
      handleToggleVisionCone,
      handleToggleHeatmap,
      handleToggleSettings,
      handleToggleDecisionPanel,
      handleHighlightClick,
      handlePreviousDecision,
      handleNextDecision,
      setSelectedCard: handleSelectCard,
      setHighlightPointIndex,
    }),
    [
      handleFullscreen,
      handleHighlightClick,
      handleNextDecision,
      handlePlayPause,
      handlePreviousDecision,
      handleProgressClick,
      handleSelectCard,
      handleToggle2DView,
      handleToggleHeatmap,
      handleToggleDecisionPanel,
      handleToggleSettings,
      handleToggleVisionCone,
      seekTo,
    ]
  );

  return {
    playbackState,
    playbackUiState,
    playbackActions,
  };
};
